import {
  PayPalScriptProvider,
  BraintreePayPalButtons,
  usePayPalScriptReducer,
  type OnApproveBraintreeActions,
  type CreateOrderBraintreeActions,
} from '@paypal/react-paypal-js';
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { buildPaypalPaymentParams, getPaypalScriptOptionsForMethod, mapPaypalSdkError } from '@/utils/paypal';
import { getAppchargeErrorMessage } from '@/utils/error';
import { SaveCardCheckbox } from '@/components/PaymentInputs/SaveCardCheckbox';
import { SavedPaypalForm } from './SavedPaypalForm';
import { getAllowDefaultCheckSavedCardBox } from '@/utils/useAllowDefaultCheckSavedCardBox';
import type { BraintreePayPalCheckoutTokenizationOptions } from '@paypal/react-paypal-js/dist/types/types/braintree/paypalCheckout';
import { PaymentMethodEnum } from '@/types/common';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { useTranslate } from '@/hooks/useTranslate';
import type { OnClickActions } from '@paypal/paypal-js';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { ErrorBoundary } from 'react-error-boundary';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { logError } from '@/utils/logger';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { PayPalButtonPlaceholder } from '@/components/Wallets/WalletButton/PayPalButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';

interface PaypalButtonUIProps {
  onReady: () => void;
  onError: (error: unknown) => void;
  createOrder: (_: unknown, actions: CreateOrderBraintreeActions) => Promise<string>;
  onApprove: (
    paymentData: BraintreePayPalCheckoutTokenizationOptions,
    actions: OnApproveBraintreeActions
  ) => Promise<void>;
  onScriptReject: () => void;
  onInvalidTaxClick?: () => void;
}

const PaypalButtonUI = ({
  onReady,
  onError,
  createOrder,
  onApprove,
  onScriptReject,
  onInvalidTaxClick,
}: PaypalButtonUIProps) => {
  const { t } = useTranslate();
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    template,
    customer,
    payment: { isFormValid },
  } = context;
  const [{ isRejected }] = usePayPalScriptReducer();

  // Block the PayPal iframe BEFORE Braintree mounts its popup. Without
  // this overlay, the iframe opens for a frame and then closes when our
  // `handleClick` rejects — visible flicker on SINGLE / PREFERRED, where
  // PayPal is the primary CTA.
  const shouldBlockClickForZipCode = !isFormValid && template.features.showBlockedZipCodeErrorInInput;

  const handleBlockedPointer = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onInvalidTaxClick?.();
  };

  const handleClick = (_: Record<string, unknown>, actions: OnClickActions) => {
    if (isFormValid) {
      trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.PAYPAL });
      return actions.resolve();
    }
    if (template.features.showBlockedZipCodeErrorInInput) {
      onInvalidTaxClick?.();
      return actions.reject();
    }
    const errorMessageKey = getZipCodeErrorMessageKey(customer);
    setError(t(errorMessageKey));
    return actions.reject();
  };

  useEffect(() => {
    if (!isRejected) return;
    onScriptReject();
  }, [isRejected, onScriptReject]);

  const paypalButton = (
    <BraintreePayPalButtons
      createOrder={createOrder}
      className="w-full !z-10"
      style={{
        layout: 'vertical',
        tagline: false,
        shape: 'rect',
        height: 48,
        color: 'blue',
      }}
      onApprove={onApprove}
      onInit={onReady}
      onError={onError}
      onClick={handleClick}
    />
  );

  return (
    <div className="relative">
      {paypalButton}
      {shouldBlockClickForZipCode && (
        <div
          data-testid="paypal-zip-blocker"
          className="absolute inset-0 w-full h-full z-[2147483647]"
          onPointerDown={handleBlockedPointer}
          onClick={handleBlockedPointer}
        />
      )}
    </div>
  );
};

interface PaypalFormProps {
  onInvalidTaxClick?: () => void;
}

export const PaypalForm = ({ onInvalidTaxClick }: PaypalFormProps) => {
  const context = useContext(CheckoutContext);
  const {
    paymentKeys,
    pricing,
    paymentMethodsSettings,
    actions: { runPayment, setError: setGlobalError },
    template,
    addressDetails,
    customer,
    checkoutConfiguration,
    featureFlags,
    card: { savedPaypalAccount },
  } = context;
  const paymentMethodSettings = paymentMethodsSettings.find((pm) => pm.paymentMethodName === PaymentMethodEnum.PAYPAL);
  const [state, setState] = useState<WalletReadyState>('loading');
  const [showZipCodeError, setShowZipCodeError] = useState(false);
  const paypalVaultEnabled = !!checkoutConfiguration?.paypalVaultEnabled;
  const { isDefaultSaveCardAllowedForLocation } = getAllowDefaultCheckSavedCardBox({
    customer,
    addressDetails,
    featureFlags,
  });
  const [saveCardChoice, setSaveCardChoice] = useState<boolean | undefined>(undefined);
  const isSaveCard = saveCardChoice ?? isDefaultSaveCardAllowedForLocation;
  const handleSetIsSaveCard = useCallback((value: boolean) => {
    setSaveCardChoice(value);
  }, []);
  const [useDifferentAccount, setUseDifferentAccount] = useState(false);
  const shouldSaveCardRef = useRef(isSaveCard);
  shouldSaveCardRef.current = isSaveCard;
  const { t } = useTranslate();
  const zipCode = addressDetails?.zipCode ?? customer.address?.zipCode;
  const handleInvalidTaxClick = useCallback(() => {
    if (template.features.showBlockedZipCodeErrorInInput) {
      setShowZipCodeError(true);
    }
    onInvalidTaxClick?.();
  }, [onInvalidTaxClick, template.features.showBlockedZipCodeErrorInInput]);
  const scriptOptions = useMemo(
    () => getPaypalScriptOptionsForMethod(paymentMethodSettings, paymentKeys, pricing.currencyCode),
    [paymentMethodSettings, paymentKeys, pricing.currencyCode]
  );

  const handleReady = () => {
    setState('ready');
  };

  const handleScriptReject = useCallback(() => {
    setState('unavailable');
  }, []);

  const handleError = (error: unknown) => {
    logError('PayPal error', error);

    const appChargeErrorType = mapPaypalSdkError(error);
    if (appChargeErrorType === null) {
      return;
    }

    setGlobalError(getAppchargeErrorMessage(t, appChargeErrorType));
  };

  const handleFatalError = (error: unknown) => {
    setState('unavailable');
    logError('PayPal error', error);
  };

  const createOrder = async (_: unknown, actions: CreateOrderBraintreeActions) => {
    return actions.braintree.createPayment(
      buildPaypalPaymentParams(pricing, paypalVaultEnabled && shouldSaveCardRef.current)
    );
  };

  const handlePaymentApprove = async (
    paymentData: BraintreePayPalCheckoutTokenizationOptions,
    actions: OnApproveBraintreeActions
  ) => {
    const tokenizationData = await actions.braintree.tokenizePayment(paymentData);
    notifyPaymentIntentClick(context, {
      paymentMethodName: PaymentMethodEnum.PAYPAL,
      isNewPaymentMethodSaved: paypalVaultEnabled && shouldSaveCardRef.current,
    });
    await runPayment(
      { tokenizationData, shouldSaveCard: paypalVaultEnabled && shouldSaveCardRef.current },
      PaymentMethodEnum.PAYPAL
    );
  };

  if (savedPaypalAccount && !useDifferentAccount) {
    if (!savedPaypalAccount.pspCardIdentifier) {
      return <PaymentMethodNotSupported />;
    }
    return <SavedPaypalForm account={savedPaypalAccount} onUseDifferentAccount={() => setUseDifferentAccount(true)} />;
  }

  if (!scriptOptions || state === 'unavailable') {
    return <PaymentMethodNotSupported />;
  }

  const zipBlock = (
    <InputTaxComponent
      isBlockedForZipCode={showZipCodeError}
      onValidate={(isValid) => {
        if (isValid && zipCode?.trim()) setShowZipCodeError(false);
      }}
    />
  );

  try {
    return (
      <div className="flex flex-col gap-4">
        {zipBlock}
        {paypalVaultEnabled && (
          <SaveCardCheckbox isSaveCard={isSaveCard} setIsSaveCard={handleSetIsSaveCard} labelKey="savePaypalCheckbox" />
        )}
        <WalletButtonShell
          state={state}
          placeholder={<PayPalButtonPlaceholder variant="blue" />}
          testId="wallet-btn-paypal-tile"
          usePlaceholderAsVisual
        >
          <ErrorBoundary fallback={<PaymentMethodNotSupported />} onError={handleFatalError}>
            <PayPalScriptProvider options={scriptOptions}>
              <PaypalButtonUI
                onReady={handleReady}
                onError={handleError}
                createOrder={createOrder}
                onApprove={handlePaymentApprove}
                onScriptReject={handleScriptReject}
                onInvalidTaxClick={handleInvalidTaxClick}
              />
            </PayPalScriptProvider>
          </ErrorBoundary>
        </WalletButtonShell>
      </div>
    );
  } catch (error) {
    logError('PayPal initialization error', error);
    return <PaymentMethodNotSupported />;
  }
};
